import { getCarrierDisplayName } from '../../../components/data-driven-logo/get-carrier-display-name';
import { STANDARD_PACKAGE_ID } from '../../../services/package-service';
import { defineMessage } from 'react-intl';

export const CUSTOM_PACKAGE_GROUP_ID = 'custom';
export const CUSTOM_PACKAGE_SET_GROUP_ID = 'custom-package-set';

const myCustomPackagesName = defineMessage({
  description: 'custom package group name',
  defaultMessage: 'My Custom Packages',
  id: 'V/yswB',
});

const myCustomPackageSetsName = defineMessage({
  description: 'custom package sets name',
  defaultMessage: 'Custom Multi-Package Sets',
  id: 'KPoCng',
});

const unknownUnit = 'Unknown';
export const allPackageTypes = {
  id: 'all',
  name: 'All Package Types',
  packageId: 'all',
  packageTypeId: 'all',
  dimensions: {
    length: 0,
    width: 0,
    height: 0,
    unit: unknownUnit,
  },
};

const createCarrierGroup = (carrier, packages) => {
  const id = carrier.carrierId;
  return {
    id,
    name: getCarrierDisplayName(id),
    packages,
  };
};

const createCustomGroup = (packages) => ({
  id: CUSTOM_PACKAGE_GROUP_ID,
  name: myCustomPackagesName,
  packages,
});

const createCustomPackageSetGroup = (packages) => ({
  id: CUSTOM_PACKAGE_SET_GROUP_ID,
  name: myCustomPackageSetsName,
  packages,
});

const createPackage = (pkg) => ({
  id: pkg.packageId,
  name: pkg.name,
  dimensions: pkg.dimensions,
  packageTypeId: pkg.packageTypeId,
});

const packageByCarrierIdOrCustom = (isPackageDropdownGenericFallbackEnabled) => (groups, pkg) => {
  const { carrierId, packageSetId, packageId } = pkg;

  if (isPackageDropdownGenericFallbackEnabled && !packageSetId && packageId === undefined) {
    return groups;
  }

  const groupKey = carrierId || (packageSetId ? CUSTOM_PACKAGE_SET_GROUP_ID : CUSTOM_PACKAGE_GROUP_ID);

  const updatedGroups = {
    ...groups,
    [groupKey]: groups[groupKey] ? [...groups[groupKey]] : [],
  };

  if (!carrierId && packageSetId) {
    updatedGroups[groupKey].push(pkg);
  } else if (!isPackageDropdownGenericFallbackEnabled && packageId === undefined) {
    updatedGroups[groupKey].push({});
  } else {
    updatedGroups[groupKey].push(createPackage(pkg));
  }

  return updatedGroups;
};

const getCarrierPackageGroups = (carriers, groupedPackages) => {
  return carriers
    .filter((carrier) => !!carrier)
    .map((carrier) => {
      const carrierPackages = groupedPackages[carrier.carrierId] || [];
      return createCarrierGroup(carrier, carrierPackages);
    });
};

const getCustomPackageGroup = (groupedPackages) => {
  const customPackages = groupedPackages[CUSTOM_PACKAGE_GROUP_ID] || [];
  const customPackageSets = groupedPackages[CUSTOM_PACKAGE_SET_GROUP_ID] || [];

  const result = [];
  if (customPackages.length) {
    result.push(createCustomGroup(customPackages));
  }
  if (customPackageSets.length) {
    result.push(createCustomPackageSetGroup(customPackageSets));
  }
  return result;
};

export const groupByCarrierAndCustom = (packages = [], carriers = [], isPackageDropdownGenericFallbackEnabled = false) => {
  const groupedPackages = packages.reduce(packageByCarrierIdOrCustom(isPackageDropdownGenericFallbackEnabled), {});
  const carrierPackageGroups = getCarrierPackageGroups(carriers, groupedPackages);
  const customPackageGroup = getCustomPackageGroup(groupedPackages);
  return [...customPackageGroup, ...carrierPackageGroups];
};

const defaultGenericPackage = {
  id: `${STANDARD_PACKAGE_ID}-false-0`,
  name: 'Package',
  packageTypeId: STANDARD_PACKAGE_ID,
};

const standardPackagesGroupName = defineMessage({
  description: 'Standard Package Group Name',
  defaultMessage: 'Standard Packages',
  id: 'q0d1x4',
});

const getGenericPackageGroup = (pkg) => ({
  id: 'package',
  name: standardPackagesGroupName,
  packages: [pkg],
});

export const groupByCustomAndAddGenericPackage = (packages = [], isPackageDropdownGenericFallbackEnabled = false) => {
  const groupedPackages = packages.reduce(packageByCarrierIdOrCustom(isPackageDropdownGenericFallbackEnabled), {});
  const customPackageGroup = getCustomPackageGroup(groupedPackages);

  const existingGenericPackage = packages.find((pkg) => pkg.packageTypeId === STANDARD_PACKAGE_ID);
  const genericPackage = existingGenericPackage ? createPackage(existingGenericPackage) : defaultGenericPackage;

  return [...customPackageGroup, getGenericPackageGroup(genericPackage)];
};
